// Internal Modules
import { getContent, getLocale } from '@/lib';
import { logger } from '@/lib/logger';
import {
  CRS_TAXPAYERID_OPTIONS,
  FPTI_INVALID_VALUE_ERROR,
  FPTI_VALUE_MISSING_ERROR,
  ITIN_PREFIX,
  TAX_PAYER_ID_MAPPER,
} from './constants';
import type { MappedTerritories } from '@/store/models/api/types';
import type { DefaultInputState } from '@/store';
import { CountryCodes } from '@/generated/graphql';

/**
 * Function used to determine which countries we should
 * display in the country dropdown
 */

// TODO: this type may not depict the whole picture.
// When the main CRS component gets typed, revisit this type
type Country = {
  countryCode: {
    value: string;
  };
  key: string;
};

type GetCountriesListParams = {
  crsTaxDetails: Country[];
  countriesList: MappedTerritories[];
  currentTaxResidencyCountry: string;
  onboardingCountry: CountryCodes;
};

const getCountriesList = ({
  crsTaxDetails,
  countriesList,
  currentTaxResidencyCountry,
  onboardingCountry,
}: GetCountriesListParams): MappedTerritories[] => {
  if (!Array.isArray(countriesList)) {
    return [];
  }

  // Countries for which the user has already provided tax details should be hidden
  // Use Set for O(1) lookup performance instead of O(m) array includes
  const hideCountriesSet = new Set(
    crsTaxDetails.map(({ countryCode }) => countryCode.value).filter(Boolean),
  );

  return countriesList
    .filter(
      c =>
        !hideCountriesSet.has(c.key as string) ||
        c.key === currentTaxResidencyCountry,
    )
    .sort((a, b) => {
      // Sort to put onboarding country first (only if present, i.e not already selected elsewhere)
      if (a.key === onboardingCountry) {
        return -1;
      }
      if (b.key === onboardingCountry) {
        return 1;
      }
      return 0;
    });
};

/**
 * getCrsTaxpayerIdType - Helper function to get tax payer
 * id type based on country code and tax payer id value
 * This method is used to check whether the TIN value is an ITIN or an SSN
 * and applies only to USA and defaults to TIN for other countries
 * @param countryCode
 * @param taxPayerIdValue
 * @returns {string} - tax payer id type
 */
const getCrsTaxpayerIdType = (
  countryCode: string,
  taxPayerIdValue: string,
): string => {
  if (countryCode === CountryCodes.Us) {
    // US-specific logic: ITINs start with '9', SSNs do not
    const cleanedValue = taxPayerIdValue.replace(/-/g, '').trim();
    return cleanedValue.startsWith(ITIN_PREFIX)
      ? CRS_TAXPAYERID_OPTIONS.ITIN
      : CRS_TAXPAYERID_OPTIONS.SSN;
  }
  // For all other countries, use the generic TIN type
  return CRS_TAXPAYERID_OPTIONS.TIN;
};

/**
 * Function responsible for updating the state in case a field
 * is empty, meanting the user has not selected or added a value
 * to it yet
 */
type UpdateStateIfEmptyParams = {
  content: string;
  fieldNameForLogging: string;
  fieldValues: DefaultInputState;
  isTaxDetailsValid: boolean;
};
const updateStateIfEmpty = ({
  fieldValues,
  fieldNameForLogging,
  content,
  isTaxDetailsValid,
}: UpdateStateIfEmptyParams): [DefaultInputState, boolean] => {
  if (fieldValues.value.trim() === '') {
    fieldValues = {
      ...fieldValues,
      displayError: true,
      error: getContent(content),
    };
    isTaxDetailsValid = false;
    logger.fpti(`crs_${fieldNameForLogging}_error`, {
      int_error_code: '1',
      int_error_desc: FPTI_VALUE_MISSING_ERROR,
      onboard_country: getLocale().country,
      transition_name: `process_${fieldNameForLogging}_validation`,
    });
    logger.info(`crs_${fieldNameForLogging}_empty`);
    return [fieldValues, isTaxDetailsValid];
  }
  return [fieldValues, isTaxDetailsValid];
};

type TaxPayerId = {
  displayError?: boolean;
  error?: string;
  value: string;
};
type GetTaxPayerIdMaxLengthParams = {
  countryCode: MappedTerritories;
  taxPayerIdValue?: TaxPayerId;
};

const getTaxPayerIdMaxLength = ({
  countryCode,
  taxPayerIdValue,
}: GetTaxPayerIdMaxLengthParams): number | undefined => {
  const { value: countryCodeValue } = countryCode || {};

  /// Default key for mapper fallback
  const DEFAULT_COUNTRY_KEY = 'DEFAULT';

  if (!countryCodeValue || !taxPayerIdValue) {
    return undefined;
  }
  const taxPayerIdType = getCrsTaxpayerIdType(
    countryCodeValue,
    taxPayerIdValue.value,
  );

  return (
    TAX_PAYER_ID_MAPPER[countryCodeValue]?.[taxPayerIdType]?.maxLength ??
    TAX_PAYER_ID_MAPPER[DEFAULT_COUNTRY_KEY]?.[taxPayerIdType]?.maxLength
  );
};

export type CrsNoTaxpayerIdReasons = {
  lawDoesNotRequireTIN: string;
  notIssued: string;
};
/// Non-localized no tax payer ID reasons for payload
const getCrsNoTaxpayerIdReasons = (): CrsNoTaxpayerIdReasons => ({
  lawDoesNotRequireTIN:
    'Country/jurisdiction’s domestic law does not require TINs to be collected/reported',
  notIssued: 'Country/jurisdiction does not issue TINs to its residents',
});

type CrsNoTaxPayerSelectOptionType = {
  text: string;
  value: string;
};

/// Localized no tax payer ID reasons for select dropdown
const getCrsNoTaxpayerIdReasonsSelectOptions =
  (): CrsNoTaxPayerSelectOptionType[] => {
    const reasons = getCrsNoTaxpayerIdReasons();
    const keys: (keyof typeof reasons)[] = Object.keys(
      reasons,
    ) as (keyof typeof reasons)[];
    return keys.map(key => {
      // Get localized content
      const localizedText = getContent(`crs.reasons.${key}`);
      if (!localizedText) {
        logger.info('no_tax_payer_id_reason_localized_text_not_found', {
          key,
        });
      }
      return {
        text: localizedText,
        value: key,
      };
    });
  };

/**
 * Function responsible for updating the state for tax payer
 * id value in case it is invalid based on TAX_PAYER_ID_MAPPER
 * map config
 */

type updateTaxPayerIdValueIfInvalidParams = {
  countryCode: DefaultInputState;
  isTaxDetailsValid: boolean;
  fieldNameForLogging: string;
  taxPayerIdValue: TaxPayerId;
};
const updateTaxPayerIdValueIfInvalid = ({
  countryCode,
  isTaxDetailsValid,
  fieldNameForLogging,
  taxPayerIdValue,
}: updateTaxPayerIdValueIfInvalidParams): [TaxPayerId, boolean] => {
  if (taxPayerIdValue.value.trim() !== '') {
    /// Get tax payer id type based on country code and tax payer id value
    const taxPayerIdType = getCrsTaxpayerIdType(
      countryCode.value,
      taxPayerIdValue.value,
    );
    const taxPayerIdValueMap =
      TAX_PAYER_ID_MAPPER[countryCode.value]?.[taxPayerIdType] ??
      TAX_PAYER_ID_MAPPER.DEFAULT?.[taxPayerIdType];
    if (
      taxPayerIdValueMap?.regex &&
      !taxPayerIdValueMap.regex.test(taxPayerIdValue.value)
    ) {
      taxPayerIdValue = {
        ...taxPayerIdValue,
        displayError: true,
        error: getContent('crs.errors.invalidTaxID.default'),
      };

      logger.fpti(`crs_${fieldNameForLogging}_error`, {
        int_error_code: '2',
        int_error_desc: FPTI_INVALID_VALUE_ERROR,
        onboard_country: getLocale().country,
        transition_name: `process_${fieldNameForLogging}_validation`,
      });
      logger.info(`crs_${fieldNameForLogging}_invalid`);

      isTaxDetailsValid = false;
    }
  }
  return [taxPayerIdValue, isTaxDetailsValid];
};

export {
  getCountriesList,
  getCrsNoTaxpayerIdReasons,
  getCrsNoTaxpayerIdReasonsSelectOptions,
  getCrsTaxpayerIdType,
  getTaxPayerIdMaxLength,
  updateStateIfEmpty,
  updateTaxPayerIdValueIfInvalid,
};
